import { useMemo } from "react";
import { flushSync } from "react-dom";

type Props = {
  config: any;
  setConfig: (v: any) => void;
  selectedTab: number;
  label: string;
  options: any[];
  hideHalfSelection?: boolean;
  hideDoubleSelection?: boolean;
};

const ModificationSelection = ({
  label,
  options,
  config,
  setConfig,
  selectedTab,
  hideHalfSelection = false,
  hideDoubleSelection = false,
}: Props) => {
  const selectedItems = useMemo(
    () => config?.[selectedTab] || [],
    [config, selectedTab],
  );

  const updateItem = (item: any, updater: (entry: any) => any) => {
    const found = (config?.[selectedTab] || []).findIndex(
      (i: any) => i.pizzaModifierId === item.pizzaModifierId,
    );

    if (found < 0) return;

    flushSync(() => {
      setConfig((e: any) => ({
        ...e,
        [selectedTab]: e[selectedTab].map((entry: any, index: number) =>
          index === found ? updater(entry) : entry,
        ),
      }));
    });
  };

  const getSelectionUnitCount = (item: any, quantity: any) =>
    Number(item?.defaultTopping || 0) === 1 && Number(quantity || 1) === 2 ? 2 : 1;

  const setRegion = (item: any, region: number | null) => {
    const nextRegion = region || 1;
    updateItem(item, (entry: any) => ({
      ...entry,
      selected: region !== null,
      pizzaRegion: nextRegion,
      PizzaRegion: nextRegion,
      quantity: region === null ? 1 : Number(entry?.quantity || 1),
      legacyQuantity: region === null ? 1 : Number(entry?.quantity || 1),
      isMultiplier: region !== null && Number(entry?.quantity || 1) > 1 ? 1 : 0,
      Multiplier: region === null ? 1 : Number(entry?.quantity || 1),
      multiplier: region === null ? 1 : Number(entry?.quantity || 1),
      DtltoppIsmultiplier: region !== null && Number(entry?.quantity || 1) > 1 ? 1 : 0,
      DtltoppMultiplier: region === null ? 1 : Number(entry?.quantity || 1),
      selectionUnitCount:
        region === null
          ? 0
          : getSelectionUnitCount(entry, Number(entry?.quantity || 1)),
      toppingUnitCount:
        region === null
          ? 0
          : getSelectionUnitCount(entry, Number(entry?.quantity || 1)),
    }));
  };

  const toggleDouble = (item: any) => {
    updateItem(item, (entry: any) => {
      const nextQuantity = Number(entry?.quantity || 1) === 2 ? 1 : 2;

      return {
        ...entry,
        selected: true,
        pizzaRegion: Number(entry?.pizzaRegion ?? entry?.PizzaRegion ?? 1),
        PizzaRegion: Number(entry?.pizzaRegion ?? entry?.PizzaRegion ?? 1),
        quantity: nextQuantity,
        legacyQuantity: nextQuantity,
        isMultiplier: nextQuantity > 1 ? 1 : 0,
        Multiplier: nextQuantity,
        multiplier: nextQuantity,
        DtltoppIsmultiplier: nextQuantity > 1 ? 1 : 0,
        DtltoppMultiplier: nextQuantity,
        selectionUnitCount: getSelectionUnitCount(
          entry,
          nextQuantity,
        ),
        toppingUnitCount: getSelectionUnitCount(
          entry,
          nextQuantity,
        ),
      };
    });
  };

  const getSelectedItem = (item: any) =>
    selectedItems.find((entry: any) => entry.pizzaModifierId === item.pizzaModifierId) ||
    item;

  const ToppingButton = ({
    active,
    tone = "choice",
    fillActive = false,
    label,
    title,
    onClick,
    children,
  }: {
    active: boolean;
    tone?: "choice" | "remove";
    fillActive?: boolean;
    label: string;
    title: string;
    onClick: () => void;
    children?: any;
  }) => {
    const activeClass =
      tone === "remove"
        ? "border-[#e11d27] bg-white text-[#e11d27] shadow-sm"
        : fillActive
        ? "border-[#16a85a] bg-[#19b964] text-white shadow-[0_4px_12px_rgba(25,185,100,0.22)]"
        : "border-[#16a85a] bg-white text-[#0f7d3c] shadow-[0_0_0_3px_rgba(25,185,100,0.12)]";

    return (
      <button
        type="button"
        aria-label={label}
        title={title}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClick();
        }}
        className={`relative h-8 w-8 shrink-0 rounded-[10px] border text-[12px] font-bold transition hover:border-[#a8a8a8] hover:bg-[#fafafa] ${
          active ? activeClass : "border-[#d6d6d6] bg-white text-[#111]"
        }`}
      >
        {children}
      </button>
    );
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto px-2 py-2 sm:px-4">
      {options.map((item, index) => {
        const selectedItem = getSelectedItem(item);
        const isSelected = !!selectedItem?.selected;
        const region = isSelected
          ? hideHalfSelection
            ? 1
            : Number(selectedItem?.pizzaRegion ?? selectedItem?.PizzaRegion ?? 1)
          : 0;
        const isDouble =
          !hideDoubleSelection && isSelected && Number(selectedItem?.quantity || 1) === 2;

        return (
        <div
          className="flex flex-col gap-2 border-b border-[#ede8df] bg-white/60 px-2.5 py-3.5 last:border-b-0 hover:bg-white sm:flex-row sm:items-center sm:justify-between"
          key={index}
        >
          <div className="min-w-0 flex-1 flex items-start justify-between gap-3">
            <p className="pr-2 text-[13px] font-medium leading-5 text-[#242424]">
              {item[label]}
              {Number(item?.defaultTopping) === 1 && (
                <span className="ml-2 inline-flex items-center rounded-full bg-[#f0ece4] px-1.5 py-[2px] text-[10px] font-medium leading-none text-[#766f64]">
                  Default
                </span>
              )}
            </p>
            {!!item.pizzaModifierPrice && (
              <p className="whitespace-nowrap text-[12px] font-semibold text-[#24513e]">
                +${Number(item.pizzaModifierPrice).toFixed(2)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 sm:pl-2">
            <ToppingButton
              active={!isSelected}
              tone="remove"
              label={`Remove ${item[label]}`}
              title="No topping"
              onClick={() => setRegion(item, null)}
            >
              <span
                className={`absolute left-1/2 top-1/2 h-[2px] w-6 -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full ${
                  !isSelected ? "bg-[#e11d27]" : "bg-[#cfcfcf]"
                }`}
              />
              <span
                className={`absolute inset-[5px] rounded-full border-2 ${
                  !isSelected ? "border-[#e11d27]" : "border-[#cfcfcf]"
                }`}
              />
            </ToppingButton>
            <ToppingButton
              active={region === 1}
              label={`${item[label]} on whole pizza`}
              title="Whole pizza"
              onClick={() => setRegion(item, 1)}
            >
              <span
                className={`absolute inset-[4px] rounded-full border-2 ${
                  region === 1
                    ? "border-[#18b95f] bg-[#18b95f]"
                    : "border-[#cfcfcf] bg-white"
                }`}
              />
              {region === 1 && (
                <span className="absolute inset-[10px] rounded-full bg-white" />
              )}
            </ToppingButton>
            {!hideHalfSelection && (
              <>
                <ToppingButton
                  active={region === 2}
                  label={`${item[label]} on left half`}
                  title="Left half"
                  onClick={() => setRegion(item, 2)}
                >
                  <span
                    className={`absolute bottom-[5px] left-[9px] top-[5px] w-[10px] rounded-l-full border-2 ${
                      region === 2
                        ? "border-[#18b95f] bg-[#18b95f]"
                        : "border-[#cfcfcf] bg-white"
                    }`}
                  />
                </ToppingButton>
                <ToppingButton
                  active={region === 3}
                  label={`${item[label]} on right half`}
                  title="Right half"
                  onClick={() => setRegion(item, 3)}
                >
                  <span
                    className={`absolute bottom-[5px] right-[9px] top-[5px] w-[10px] rounded-r-full border-2 ${
                      region === 3
                        ? "border-[#18b95f] bg-[#18b95f]"
                        : "border-[#cfcfcf] bg-white"
                    }`}
                  />
                </ToppingButton>
              </>
            )}
            {!hideDoubleSelection && (
              <ToppingButton
                active={isDouble}
                fillActive
                label={`Toggle double ${item[label]}`}
                title="Double topping"
                onClick={() => toggleDouble(item)}
              >
                2x
              </ToppingButton>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default ModificationSelection;
